"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { scrollToFirstError } from "@wtc/ui";
import { zodResolver } from "@/lib/utils/validation/zod-react-hook-form-resolver";
import * as z from "zod";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { TagManager } from "./components/TagManager";
import { NoteFormFields } from "./components/NoteFormFields";
import {
  StickyNote,
  Clock,
  AlertCircle,
  Calendar,
  Phone,
  Mail,
  FileText,
  Eye,
  EyeOff,
} from "lucide-react";
import { NOTE_TYPE_VALUES } from "@/lib/constants/note-types";

// Note-specific schema - comprehensive with all database fields
const noteSchema = z.object({
  title: z.string().optional(),
  content: z.string().min(1, "Note content cannot be empty."),
  // contact_notes.note_type enum (codegen-backed). `follow_up` is NOT a valid
  // value — offering it produced a DB enum-reject on save.
  note_type: z.enum(NOTE_TYPE_VALUES),
  tags: z.array(z.string()).optional(),
  is_internal: z.boolean(),
});

type NoteFormValues = z.infer<typeof noteSchema>;

interface NoteFormProps {
  contactId: string;
  userId: string;
  onSubmit: (values: NoteFormValues) => Promise<void>;
  isSubmitting: boolean;
}

const NOTE_TYPE_OPTIONS = [
  {
    value: "general",
    label: "General Note",
    icon: StickyNote,
    description: "General information or observation",
  },
  {
    value: "proposal",
    label: "Proposal",
    icon: Clock,
    description: "Notes about a proposal or quote",
  },
  {
    value: "meeting",
    label: "Meeting Notes",
    icon: Calendar,
    description: "Notes from a meeting or call",
  },
  {
    value: "call",
    label: "Call Notes",
    icon: Phone,
    description: "Notes from a phone conversation",
  },
  {
    value: "email",
    label: "Email Notes",
    icon: Mail,
    description: "Notes about email correspondence",
  },
  {
    value: "internal",
    label: "Internal Note",
    icon: FileText,
    description: "Internal team notes (not visible to client)",
  },
];

export const NoteForm: React.FC<NoteFormProps> = ({
  contactId,
  userId,
  onSubmit,
  isSubmitting,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);

  const form = useForm<NoteFormValues>({
    resolver: zodResolver(noteSchema as any),
    defaultValues: {
      title: "",
      content: "",
      note_type: "general",
      tags: [],
      is_internal: false,
    },
  });

  const watchedNoteType = form.watch("note_type");
  const watchedIsInternal = form.watch("is_internal");

  const handleSubmit = async (values: NoteFormValues) => {
    setError(null);
    try {
      // Include tags in the submission
      const noteData = {
        ...values,
        tags,
        // Auto-set internal flag if note type is internal
        is_internal: values.note_type === "internal" ? true : values.is_internal,
      };
      await onSubmit(noteData);
      form.reset();
      setTags([]);
    } catch (err: any) {
      setError(err.message || "Failed to create note");
    }
  };

  // Auto-set internal flag when internal note type is selected
  const handleNoteTypeChange = (noteType: string) => {
    form.setValue("note_type", noteType as any);
    if (noteType === "internal") {
      form.setValue("is_internal", true);
    }
  };

  const selectedNoteType = NOTE_TYPE_OPTIONS.find((type) => type.value === watchedNoteType);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit, scrollToFirstError)} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg dark:bg-red-950/40 dark:text-red-300">
            <AlertCircle size={16} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <NoteFormFields
          form={form}
          isSubmitting={isSubmitting}
          noteTypeOptions={NOTE_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label, description: o.description }))}
          onNoteTypeChange={handleNoteTypeChange}
          watchedNoteType={watchedNoteType}
          watchedIsInternal={watchedIsInternal}
        />

        <TagManager tags={tags} setTags={setTags} disabled={isSubmitting} />

        <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="flex items-center gap-1">
              {selectedNoteType && <selectedNoteType.icon size={12} />}
              {selectedNoteType?.label}
            </Badge>
            {watchedIsInternal && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <EyeOff size={12} />
                Internal
              </Badge>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isSubmitting} className="min-w-[120px]">
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Saving...</span>
              </>
            ) : (
              "Save Note"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
